import { useEffect, useState, useRef, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { api, formatApiError, fileUrl } from "../lib/api";
import { toast } from "sonner";
import { Sparkles, Upload, Trash2, BellRing, Undo2, BookmarkPlus } from "lucide-react";
import AICaption from "./AICaption";
import { hasSocial } from "../lib/services";
import { useConfirm } from "./ConfirmProvider";

const REQUIRED = { client_id: "Client", content_type: "Content Type", platform: "Platform", content_topic: "Content Topic", publishing_date: "Publishing Date" };

const EMPTY = {
  client_id:"", content_type:"Instagram Post", platform:"Instagram", content_topic:"", content_pillar:"",
  caption:"", copy:"", personas:"", creative_url:"", reel_url:"", cta:"", hashtags:"",
  assigned_copywriter:"", assigned_designer:"", assigned_video_editor:"",
  publishing_date:"", publishing_time:"", status:"Draft",
};

const TYPES = ["Instagram Post","Carousel","Reel","Story","LinkedIn Post","Facebook Post","YouTube Video","YouTube Short","Blog","Ad Creative","Other"];
const PLATFORMS = ["Instagram","Facebook","LinkedIn","YouTube","Twitter/X","TikTok","Meta Ads","Website"];
const PILLARS = ["Education","Behind The Scenes","Product","Testimonial","Trend","Announcement","Community","Promo"];
const STATUSES = ["Draft","Pending Approval","Approved","Scheduled","Published","Changes Required"];

export default function ContentForm({ open, onOpenChange, initial, clients: allClients, onSaved }) {
  const clients = useMemo(() => (allClients || []).filter(c => hasSocial(c) || c.id === initial?.client_id), [allClients, initial]);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [personaSaving, setPersonaSaving] = useState(false);
  const fileRef = useRef(null);
  const confirm = useConfirm();
  const [errors, setErrors] = useState({});

  useEffect(()=>{
    setErrors({});
    setForm(initial ? {...EMPTY, ...initial} : {...EMPTY, client_id: clients?.[0]?.id || ""});
  }, [initial, open]);

  const set = (k,v) => setForm(f => ({...f, [k]: v}));
  const activeClient = useMemo(()=>clients?.find(c=>c.id===form.client_id), [clients, form.client_id]);
  const personaOptions = activeClient?.personas_library || [];

  const savePersonaToLibrary = async () => {
    const p = (form.personas || "").trim();
    if (!p || !form.client_id) return;
    if (personaOptions.some(x => x.toLowerCase() === p.toLowerCase())) { toast.info("Already in library"); return; }
    setPersonaSaving(true);
    try {
      await api.post(`/clients/${form.client_id}/personas`, { persona: p });
      toast.success(`"${p}" saved to library`);
      onSaved?.();  // refresh clients so future opens see the new persona
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    finally { setPersonaSaving(false); }
  };

  const save = async () => {
    const errs = {};
    Object.entries(REQUIRED).forEach(([k, label]) => { if (!String(form[k] || "").trim()) errs[k] = `${label} is required.`; });
    setErrors(errs);
    if (Object.keys(errs).length) { toast.error(Object.values(errs)[0]); return; }
    setBusy(true);
    try {
      let saved;
      if (initial?.id) {
        saved = (await api.put(`/contents/${initial.id}`, form)).data;
        toast.success("Content updated");
      } else {
        saved = (await api.post("/contents", form)).data;
        toast.success(`Content created for ${saved?.publishing_date || form.publishing_date}`);
      }
      onOpenChange(false);
      onSaved?.(saved);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!initial?.id) return;
    if (!(await confirm({ title: "Are you sure you want to delete this content?", description: "This post will be removed from the calendar. This cannot be undone." }))) return;
    await api.delete(`/contents/${initial.id}`);
    toast.success("Deleted");
    onOpenChange(false); onSaved?.();
  };

  const nudge = async () => {
    if (!initial?.id) return;
    try {
      const { data } = await api.post(`/contents/${initial.id}/nudge-approval`);
      toast.success(`Reminder sent to ${data.notified} client user${data.notified === 1 ? "" : "s"}`);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Nudge failed");
    }
  };

  const revertToDraft = async () => {
    if (!initial?.id) return;
    if (!(await confirm({ title: `Move this ${form.status} post back to Draft?`, description: "It will disappear from the client's approval queue and schedule.", confirmText: "Move to Draft", destructive: false }))) return;
    try {
      await api.post(`/contents/${initial.id}/revert-to-draft`);
      toast.success("Moved to Draft");
      onOpenChange(false); onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Move failed");
    }
  };

  const uploadFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: {"Content-Type": "multipart/form-data"} });
      const key = f.type.startsWith("video/") ? "reel_url" : "creative_url";
      set(key, data.path);
      toast.success("Uploaded");
    } catch (err) {
      toast.error("Upload failed");
    } finally { setUploading(false); }
  };

  const clientName = clients?.find(c=>c.id===form.client_id)?.client_name || "";

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#FFFFFF] border-[#E4E7EC] max-w-4xl max-h-[88vh] overflow-y-auto" data-testid="content-form-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center justify-between">
            <span>{initial?.id ? "Edit Content" : "New Content"}</span>
            {initial?.content_id && <span className="text-[11px] font-mono text-neutral-500">{initial.content_id}</span>}
          </DialogTitle>
        </DialogHeader>
        {clients.length === 0 && (
          <div className="text-xs rounded-md border border-amber-300 bg-amber-50 text-amber-800 p-3" data-testid="ctf-no-clients">
            No social media clients are assigned to you yet, so there is nothing to add content for. Ask an admin to assign you to a client.
          </div>
        )}
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Client" required error={errors.client_id}>
            <Select value={form.client_id} onValueChange={v=>set("client_id", v)}>
              <SelectTrigger className="bg-[#FFFFFF] border-[#E4E7EC]" data-testid="ctf-client"><SelectValue placeholder="Pick client"/></SelectTrigger>
              <SelectContent className="bg-[#FFFFFF] border-[#E4E7EC]">{clients?.map(c=><SelectItem key={c.id} value={c.id}>{c.client_name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Content Type" required error={errors.content_type}>
            <Select value={form.content_type} onValueChange={v=>set("content_type", v)}>
              <SelectTrigger className="bg-[#FFFFFF] border-[#E4E7EC]" data-testid="ctf-type"><SelectValue/></SelectTrigger>
              <SelectContent className="bg-[#FFFFFF] border-[#E4E7EC]">{TYPES.map(x=><SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Platform" required error={errors.platform}>
            <Select value={form.platform} onValueChange={v=>set("platform", v)}>
              <SelectTrigger className="bg-[#FFFFFF] border-[#E4E7EC]" data-testid="ctf-platform"><SelectValue/></SelectTrigger>
              <SelectContent className="bg-[#FFFFFF] border-[#E4E7EC]">{PLATFORMS.map(x=><SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Content Pillar" className="hidden">
            <Select value={form.content_pillar} onValueChange={v=>set("content_pillar", v)}>
              <SelectTrigger className="bg-[#FFFFFF] border-[#E4E7EC]" data-testid="ctf-pillar-dup"><SelectValue placeholder="Pick pillar"/></SelectTrigger>
              <SelectContent className="bg-[#FFFFFF] border-[#E4E7EC]">{PILLARS.map(x=><SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Content Topic (Visual Direction / Idea)" span2 required error={errors.content_topic}>
            <Input value={form.content_topic} onChange={e=>set("content_topic", e.target.value)} data-testid="ctf-topic" className="bg-[#FFFFFF] border-[#E4E7EC]"/>
          </Field>
          <Field label="Personas">
            <div className="flex gap-1.5">
              <Input
                list={form.client_id ? `personas-${form.client_id}` : undefined}
                value={form.personas}
                onChange={e=>set("personas", e.target.value)}
                data-testid="ctf-personas"
                placeholder={personaOptions.length ? "Pick or type…" : "e.g. Young professionals"}
                className="bg-[#FFFFFF] border-[#E4E7EC] flex-1"
              />
              {form.client_id && (
                <datalist id={`personas-${form.client_id}`}>
                  {personaOptions.map(p => <option key={p} value={p}/>)}
                </datalist>
              )}
              {form.personas && !personaOptions.some(p => p.toLowerCase() === form.personas.trim().toLowerCase()) && (
                <Button type="button" variant="outline" onClick={savePersonaToLibrary} disabled={personaSaving} data-testid="ctf-save-persona" className="border-[#E4E7EC] shrink-0" title="Save to client's persona library">
                  <BookmarkPlus className="w-4 h-4"/>
                </Button>
              )}
            </div>
            {personaOptions.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1">
                {personaOptions.map(p => (
                  <button key={p} type="button" onClick={()=>set("personas", p)} data-testid={`persona-chip-${p}`}
                    className={`text-[10px] font-mono px-2 py-0.5 rounded-full border transition-colors ${form.personas === p ? "bg-orange-100 text-orange-700 border-orange-300" : "bg-white text-neutral-600 border-neutral-200 hover:border-neutral-400"}`}>
                    {p}
                  </button>
                ))}
              </div>
            )}
          </Field>
          <Field label="Topical / Bucket">
            <Select value={form.content_pillar} onValueChange={v=>set("content_pillar", v)}>
              <SelectTrigger className="bg-[#FFFFFF] border-[#E4E7EC]" data-testid="ctf-pillar"><SelectValue placeholder="Pick bucket"/></SelectTrigger>
              <SelectContent className="bg-[#FFFFFF] border-[#E4E7EC]">{PILLARS.map(x=><SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Copy (on-creative text)" span2>
            <Textarea rows={2} value={form.copy} onChange={e=>set("copy", e.target.value)} data-testid="ctf-copy" placeholder="Text that goes on the visual" className="bg-[#FFFFFF] border-[#E4E7EC]"/>
          </Field>
          <Field label="Caption" span2>
            <div className="flex gap-2 items-start">
              <Textarea rows={4} value={form.caption} onChange={e=>set("caption", e.target.value)} data-testid="ctf-caption" className="bg-[#FFFFFF] border-[#E4E7EC] flex-1"/>
              <Button type="button" onClick={()=>setAiOpen(true)} data-testid="ctf-ai-btn" className="bg-gradient-to-br from-orange-500 to-red-600 hover:opacity-90 text-neutral-900 shrink-0">
                <Sparkles className="w-4 h-4 mr-1"/> AI
              </Button>
            </div>
          </Field>
          <Field label="CTA"><Input value={form.cta} onChange={e=>set("cta", e.target.value)} data-testid="ctf-cta" className="bg-[#FFFFFF] border-[#E4E7EC]"/></Field>
          <Field label="Hashtags"><Input value={form.hashtags} onChange={e=>set("hashtags", e.target.value)} data-testid="ctf-hashtags" placeholder="#brand #growth" className="bg-[#FFFFFF] border-[#E4E7EC]"/></Field>
          <Field label="Creative / Reel Upload" span2>
            <div className="space-y-3">
              <div className="flex gap-3 items-center flex-wrap">
                <input ref={fileRef} type="file" accept="image/*,video/*" onChange={uploadFile} className="hidden" data-testid="ctf-file-input"/>
                <Button type="button" variant="outline" onClick={()=>fileRef.current?.click()} disabled={uploading} data-testid="ctf-upload-btn" className="border-[#E4E7EC]">
                  <Upload className="w-4 h-4 mr-1"/> {uploading ? "Uploading..." : (form.creative_url || form.reel_url) ? "Replace" : "Upload"}
                </Button>
                {(form.creative_url || form.reel_url) && (
                  <Button type="button" variant="ghost" onClick={()=>{ set("creative_url", ""); set("reel_url", ""); }} className="text-rose-400 hover:text-rose-700" data-testid="ctf-remove-creative">
                    <Trash2 className="w-4 h-4 mr-1"/> Remove
                  </Button>
                )}
              </div>
              {form.creative_url && (
                <div className="rounded-lg border border-[#E4E7EC] bg-[#FFFFFF] p-2 max-w-md" data-testid="ctf-creative-preview">
                  <img src={fileUrl(form.creative_url)} alt="creative preview" className="w-full max-h-80 object-contain rounded-md"/>
                </div>
              )}
              {form.reel_url && (
                <div className="rounded-lg border border-[#E4E7EC] bg-[#FFFFFF] p-2 max-w-md" data-testid="ctf-video-preview">
                  <video src={fileUrl(form.reel_url)} controls className="w-full max-h-80 rounded-md" preload="metadata"/>
                </div>
              )}
            </div>
          </Field>
          <Field label="Assigned Copywriter"><Input value={form.assigned_copywriter} onChange={e=>set("assigned_copywriter", e.target.value)} data-testid="ctf-copywriter" className="bg-[#FFFFFF] border-[#E4E7EC]"/></Field>
          <Field label="Assigned Designer"><Input value={form.assigned_designer} onChange={e=>set("assigned_designer", e.target.value)} data-testid="ctf-designer" className="bg-[#FFFFFF] border-[#E4E7EC]"/></Field>
          <Field label="Assigned Video Editor"><Input value={form.assigned_video_editor} onChange={e=>set("assigned_video_editor", e.target.value)} data-testid="ctf-editor" className="bg-[#FFFFFF] border-[#E4E7EC]"/></Field>
          <Field label="Status">
            <Select value={form.status} onValueChange={v=>set("status", v)}>
              <SelectTrigger className="bg-[#FFFFFF] border-[#E4E7EC]" data-testid="ctf-status"><SelectValue/></SelectTrigger>
              <SelectContent className="bg-[#FFFFFF] border-[#E4E7EC]">{STATUSES.map(x=><SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Publishing Date" required error={errors.publishing_date}><Input type="date" value={form.publishing_date} onChange={e=>set("publishing_date", e.target.value)} data-testid="ctf-date" className="bg-[#FFFFFF] border-[#E4E7EC]"/></Field>
          <Field label="Publishing Time"><Input type="time" value={form.publishing_time} onChange={e=>set("publishing_time", e.target.value)} data-testid="ctf-time" className="bg-[#FFFFFF] border-[#E4E7EC]"/></Field>
        </div>
        <div className="text-[10px] font-mono text-neutral-500"><span className="text-rose-500">*</span> Mandatory fields</div>
        <DialogFooter className="gap-2 flex-wrap">
          {initial?.id && <Button variant="ghost" onClick={remove} data-testid="ctf-delete" className="text-rose-400 hover:text-rose-700 mr-auto"><Trash2 className="w-4 h-4 mr-1"/>Delete</Button>}
          {initial?.id && initial?.status === "Pending Approval" && (
            <Button type="button" variant="outline" onClick={nudge} data-testid="ctf-nudge" className="border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100">
              <BellRing className="w-4 h-4 mr-1"/> Send Reminder
              {initial?.nudge_count > 0 && <span className="ml-1 text-[10px] font-mono opacity-70">·{initial.nudge_count}</span>}
            </Button>
          )}
          {initial?.id && !["Draft", "Published"].includes(initial?.status || "") && (
            <Button type="button" variant="outline" onClick={revertToDraft} data-testid="ctf-revert-draft" className="border-neutral-300">
              <Undo2 className="w-4 h-4 mr-1"/> Move to Draft
            </Button>
          )}
          <Button variant="ghost" onClick={()=>onOpenChange(false)} data-testid="ctf-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="ctf-save">
            {busy ? "Saving..." : "Save Content"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <AICaption open={aiOpen} onOpenChange={setAiOpen} topic={form.content_topic} platform={form.platform} clientName={clientName} onApply={(res)=>{
      set("caption", res.caption || form.caption);
      if (res.cta) set("cta", res.cta);
      if (res.hashtags) set("hashtags", res.hashtags);
      setAiOpen(false);
    }}/>
    </>
  );
}

function Field({label, span2, required, error, children}) {
  return (
    <div className={`space-y-1.5 ${span2?"md:col-span-2":""}`}>
      <Label className="text-[10px] uppercase font-mono tracking-wider text-neutral-500">{label}{required && <span className="text-rose-500 ml-0.5">*</span>}</Label>
      {children}
      {error && <div className="text-[11px] text-rose-600" data-testid="field-error">{error}</div>}
    </div>
  );
}
